import React, { useState } from "react";
import {
  Card,
  Space as AntdSpace,
  Button,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  DatePicker,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import {
  useReportsServiceDownloadAuditLog2Report,
  useReportsServiceGetAuditLog2Report,
} from "api/queries";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;

const PERIOD_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "thisWeek" },
  { label: "This Month", value: "thisMonth" },
  { label: "This Year", value: "thisYear" },
];

const getPeriodRange = (period: string): [dayjs.Dayjs, dayjs.Dayjs] => {
  const now = dayjs();
  switch (period) {
    case "today":
      return [now.startOf("day"), now.endOf("day")];
    case "thisWeek":
      return [now.startOf("week"), now.endOf("week")];
    case "thisMonth":
      return [now.startOf("month"), now.endOf("month")];
    case "thisYear":
      return [now.startOf("year"), now.endOf("year")];
    default:
      return [now.subtract(1, "year"), now];
  }
};

const SystemLog = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "year"));
  const [endDate, setEndDate] = useState(dayjs());
  const [period, setPeriod] = useState<string>("");

  const offset = (page - 1) * limit;

  const { data, isLoading } = useReportsServiceGetAuditLog2Report({
    limit,
    offset,
    search,
    startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
  });

  const { mutate: downloadLog, isPending: isDownloadPending } =
    useReportsServiceDownloadAuditLog2Report();

  const columns = [
    {
      title: "User",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Action",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
    },
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      key: "ipAddress",
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp: string) =>
        dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss"),
    },
  ];

  const handleTableChange = (pagination: any) => {
    if (pagination && pagination.current && pagination.pageSize) {
      setPage(pagination.current || 1);
      setLimit(pagination.pageSize || 10);
    }
  };

  const handleDownload = () => {
    downloadLog(
      {
        search,
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: any) => {
          if (response?.downloadUrl) {
            window.open(response.downloadUrl, "_blank");
          }
        },
      }
    );
  };

  const handlePeriodChange = (value: string) => {
    setPeriod(value);
    const [start, end] = getPeriodRange(value);
    setStartDate(start);
    setEndDate(end);
    setPage(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <AntdSpace direction="vertical" size="large" style={{ width: "100%" }}>
        <AntdSpace style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>System Log</Title>
          <AntdSpace>
            <Select
              value={period}
              onChange={handlePeriodChange}
              placeholder="Period"
              style={{ width: 120 }}
              allowClear
            >
              {PERIOD_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            <DatePicker
              value={startDate}
              onChange={(date) => {
                if (date) {
                  setStartDate(date);
                  setPeriod("");
                  setPage(1);
                }
              }}
              style={{ marginRight: 8 }}
              allowClear={false}
              placeholder="From"
            />
            <DatePicker
              value={endDate}
              onChange={(date) => {
                if (date) {
                  setEndDate(date);
                  setPeriod("");
                  setPage(1);
                }
              }}
              allowClear={false}
              placeholder="To"
            />
            <Input.Search
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search user, action, or module"
              style={{ width: 200 }}
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloadPending}
              onClick={handleDownload}
            >
              Download Log
            </Button>
          </AntdSpace>
        </AntdSpace>
        {/* Summary Statistics */}
        <Row gutter={16}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Logs"
                value={data?.kpis?.totalLogs || 0}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Module: Reports"
                value={
                  data?.items?.filter((l: any) => l.module === "reports")
                    .length || 0
                }
                loading={isLoading}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Unique Users"
                value={
                  new Set((data?.items || []).map((l: any) => l.username)).size
                }
                loading={isLoading}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
        </Row>
        {/* Main Data Table */}
        <Table
          columns={columns}
          dataSource={data?.items || []}
          pagination={{
            current: page,
            pageSize: limit,
            total: data?.total || 0,
            onChange: (page, pageSize) => {
              setPage(page);
              setLimit(pageSize);
            },
            pageSizeOptions: [10, 20, 50, 100],
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          loading={isLoading}
          scroll={{ x: "max-content" }}
          rowKey={(item) => item.idAuditLog2}
          onChange={handleTableChange}
        />
      </AntdSpace>
    </motion.div>
  );
};

export default SystemLog;
